/**
 * @scb/schema — zod definitions for the rider/preset JSON contract.
 *
 * Source of truth for this file is `docs/RIDER_SCHEMA_SPEC.md` (the strict target shape,
 * schema_version=2 / post uuid-migration) plus the formal `docs/schemas/rider.schema.json`.
 * `docs/schemas/rider.schema.json` is now *generated from this file* (see
 * `scripts/generate-json-schema.cjs` / the `generate:json-schema` npm script) so there is
 * exactly one hand-maintained source of truth.
 *
 * Scope note: this is the STRICT v2 write-side contract (`schema_version: 2`, every node has a
 * persistent `uuid`, every list/gear item has `owner_uuid`). It intentionally does NOT implement
 * v1-tolerant reading, the real uuid/owner_uuid *assignment* migration for the 752 legacy riders,
 * or any DB/import concerns — that's Phase 4 (db-migrator/backend-lead), see CLAUDE.md and
 * RIDER_SCHEMA_SPEC.md §8. `validateRider()`/`validatePreset()` (see `validate.ts`) do apply a
 * couple of narrow, explicitly-spec'd "strip known-benign leaks" normalizations (RIDER_SCHEMA_SPEC
 * §6 steps 4/6) as validator ergonomics, not migration.
 */
import { z } from "zod";
import { MixingSystemSchema, PASchema } from "./system";
// ---------------------------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------------------------
/** RFC-4122 UUID (any version — the corpus/spec doesn't mandate v4 specifically at parse time). */
export const RIDER_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuidField = () => z.string().regex(RIDER_UUID_PATTERN, "must be an RFC-4122 UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)");
export const UuidSchema = uuidField();
/**
 * TODO(sound-engineer): tighten to a closed `z.enum([...])` once the full corpus vocabulary is
 * confirmed (RIDER_SCHEMA_SPEC.md §4.1, §9 — "Type enum-vs-open-string"). For now this stays an
 * open, length-capped string so real rows using an unlisted `Type` value never hard-fail
 * validation/import. Suggested vocabulary (not enforced): input — mic, DI, close, top, bottom,
 * in, out, Headset, rad/mic RADIO, Wireless, Body Pack; output — mono, stereoL, stereoR,
 * matrix mono, send.
 */
export const AudioListItemTypeSchema = z.string().max(50);
/**
 * The 13 `Category` values observed covering 7,328/7,329 sampled gear items
 * (RIDER_TYPING_DATA.md §3.4). Exported for reference/future enum use and for a non-blocking
 * "is this a known category" helper — NOT enforced as a closed type yet, see
 * `GearCategorySchema` below.
 */
export const KNOWN_GEAR_CATEGORIES = [
    "Wire",
    "Microphones and DI",
    "Instruments and access",
    "Monitoring",
    "Power distribution",
    "Stands",
    "Backline",
    "P.A.",
    "Venue Mixing and PA",
    "FOH mixing system",
    "Stage Gears",
    "Stage construction",
    "Mixing Consoles",
    /**
     * РАСХОДНИКИ — рубрика, добавленная 30.09.2026 по решению владельца («заводи расходники в
     * категории гира»), а не найденная в корпусе v1: там её нет, потому что v1 расходников не считал.
     *
     * Понадобилась, когда комплект радиосистемы стал единицей заказа: приёмник, передатчик и капсюль
     * едут вместе, а батарейки к ним — нет. Их считают на шоу и заказывают отдельной строкой,
     * потому что часть прокатчиков их привозит, а часть перекладывает на организатора, и на этом
     * обжигаются («на этом можно обжечься», 30.09.2026).
     *
     * Добавление безопасно: список и без того не закрытый перечень, а справочник — `Category`
     * остаётся открытой строкой (см. `GearCategorySchema` ниже).
     */
    "Consumables",
];
export function isKnownGearCategory(value) {
    return KNOWN_GEAR_CATEGORIES.includes(value);
}
/**
 * TODO(sound-engineer): tighten to `z.enum(KNOWN_GEAR_CATEGORIES)` once enum completeness is
 * confirmed against the full 752-rider corpus, not just the sample (RIDER_SCHEMA_SPEC.md §4.2,
 * §9). For now: open, length-capped string so a stray/dirty legacy category (observed: an empty
 * string, "Stage NEW") doesn't hard-block validation. Use `isKnownGearCategory()` for a
 * non-blocking warning in callers that want one.
 */
export const GearCategorySchema = z.string().max(100);
const KNOWN_OWNER_VALUES = ["Rent", "Band"];
/**
 * TODO(sound-engineer): tighten to `z.enum(["Rent", "Band"])` once the dirty legacy values
 * (`""`, `"BAND"`, ... — RIDER_TYPING_DATA.md anomaly #7) have a product decision on backfill
 * (RIDER_SCHEMA_SPEC.md §4.2, §8.6, §9 — this is a marketplace/warehouse-semantics decision, not
 * a cosmetic one, so must NOT be silently rewritten wholesale). For now: accept any string, but
 * normalize obvious case-only duplicates ("band", "BAND", " Rent " -> "Band"/"Rent") so real rows
 * that only differ by casing/whitespace validate cleanly; anything else (e.g. `""`) passes through
 * unchanged and is left for the sign-off'd migration to resolve.
 */
export const OwnerSchema = z.preprocess((value) => {
    if (typeof value === "string") {
        const trimmed = value.trim();
        const known = KNOWN_OWNER_VALUES.find((candidate) => candidate.toLowerCase() === trimmed.toLowerCase());
        if (known)
            return known;
    }
    return value;
}, z.string().max(50));
const KNOWN_PHANTOM_POWER_VALUES = ["On", "Off"];
/**
 * `Phantom_power` is a genuinely closed 2-value enum (unlike Type/Category/Owner, no sign-off
 * needed on vocabulary), but real corpus data was found (during this package's own corpus test,
 * `test/corpus.spec.ts` against `rider_65`) using lowercase `"on"` — normalize case rather than
 * hard-reject, same "normalize obvious casing dupes" treatment the spec calls for on `Type`
 * (RIDER_SCHEMA_SPEC.md §4.1).
 */
export const PhantomPowerSchema = z.preprocess((value) => {
    if (typeof value === "string") {
        const known = KNOWN_PHANTOM_POWER_VALUES.find((candidate) => candidate.toLowerCase() === value.toLowerCase());
        if (known)
            return known;
    }
    return value;
}, z.enum(KNOWN_PHANTOM_POWER_VALUES));
// ---------------------------------------------------------------------------------------------
// List item shapes (three related-but-distinct shapes per RIDER_SCHEMA_SPEC.md §4: input
// audio-list item, output audio-list item, gear item — gear item further splits into a
// rider-instantiated variant (owner_uuid required) and a preset variant (owner_uuid forbidden,
// §2.4/§4.2)).
// ---------------------------------------------------------------------------------------------
const audioListItemCommonShape = {
    Source: z.string().max(255),
    Type: AudioListItemTypeSchema,
    ChName: z.string().max(100),
    Comment: z.string().max(2000),
    Channel: z
        .number()
        .int()
        .min(1)
        .describe("Uniqueness across the WHOLE rider tree is a semantic rule — see validateRider()'s semantic checks, not expressible per-item."),
    Locked: z.boolean(),
};
/**
 * Set on a rider (never a preset) `input_list`/`output_list` item to mark it a PLACEHOLDER left
 * behind by "Keep existing I/O" on object deletion (docs/V2_IO_KEEP_ON_DELETE.md): the line has no
 * owning object anymore, but its `Channel` slot is deliberately held open. Absent/false = a normal,
 * connected line (every one of the 720 imported legacy riders + every prior v2-authored rider omits
 * this field, so it defaults falsy). An `Unconnected` line is always root-owned — see
 * `validateRider()`'s semantic check — and still counts for `Channel` uniqueness/`nextChannel` like
 * any other line (docs/V2_IO_KEEP_ON_DELETE.md §1).
 */
const unconnectedField = () => z.boolean().optional();
/**
 * SCRUM-552 (contract 2.12.0, owner 04.10.2026) — "Link": marks the FIRST line of a stereo pair.
 * It means "linked with the line whose `Channel` is this one's + 1, in the SAME list (input or
 * output), across the whole rider" — a pair is always two consecutive channels, N and N+1, wherever
 * their owning objects sit. The second line carries nothing; it is found by number.
 *
 * This is NOT `linked_uuids`: that holds OBJECT uuids of other nodes fed by the same line, and an
 * audio-list line has no id of its own to point at. Linking never rewrites names (two different names
 * stay different) and never changes a count — a pair is two mono halves, counted as two. Absent =
 * not the first line of a pair (every document stored before 2.11.0). Only the literal `true` is
 * stored; unlinking deletes the key. `validateRider()` checks the partner exists and is not itself a
 * first line.
 */
const stereoLinkField = () => z.literal(true).optional();
/**
 * SCRUM-170 — "shared I/O": other objects on the stage that are physically/logically fed by this
 * SAME line (e.g. two monitors both carrying the same mix, whether via a mono/split amp or linked
 * actives on different physical outs) without giving up single-owner `owner_uuid` (the v1
 * audio-list bug fix, docs/BUG_AUDIO_LISTS.md — one node still REALLY owns/creates the row). Rider-
 * side only: preset variants below intentionally omit this, same reasoning as `owner_uuid` — a
 * preset isn't instantiated yet, so there are no live sibling uuids to link to.
 */
const linkedUuidsField = () => z.array(uuidField()).optional();
/**
 * The PRESET form of `linked_uuids` (SCRUM-170). A preset carries no uuids at all — its nodes'
 * `uuid` is optional and "save as preset" deliberately drops identity (see
 * `apps/web/src/editor/preset-export.ts`) — so a shared mix inside a preset is referenced by
 * POSITION instead: the index of the linked node in a PRE-ORDER walk of the preset (root = 0, then
 * each child's subtree in `children` order). `insertPresetAsChild` mints uuids in that same order,
 * so it can turn these back into real `linked_uuids` at drop time.
 *
 * Without it, saving "two monitors on one mix" as a preset silently lost the link and dropped back
 * in with one monitor owning the output and the other connected to nothing (owner report
 * 2026-08-10). Links pointing OUTSIDE the saved subtree cannot be represented and are dropped at
 * save time — there is nothing in the preset for them to mean.
 */
const linkedIndicesField = () => z.array(z.number().int().min(0)).optional();
/** `input_list` entry on a rider node. `Phantom_power` is only ever meaningful here. */
export const InputListItemSchema = z
    .object({
    ...audioListItemCommonShape,
    Phantom_power: PhantomPowerSchema.optional(),
    owner_uuid: uuidField(),
    Unconnected: unconnectedField(),
    linked_uuids: linkedUuidsField(),
    Stereo_link: stereoLinkField(),
    /**
     * ССЫЛКА «ЧЕМУ СЛУЖИТ» — на СТРОКУ ГИРА, которой снимают этот канал (SCRUM-639, шаг 2).
     *
     * Третья из трёх связей, которые до сих пор ездили на одном механизме (состав предмета, набор
     * на сцену, назначение). Её не было вовсе: микрофон канала опознавался ПОДБОРОМ ПО ИМЕНИ
     * (`apps/web/src/editor/channel-gear.ts#twinIndex`), то есть сравнением текста — а текст из v1
     * написан людьми в десяти написаниях.
     *
     * ЧТО ЭТО СТОИТ СЕГОДНЯ, замер на деве 18.10.2026 по 13 706 каналам: 10 633 стоят у предмета с
     * ЕДИНСТВЕННЫМ микрофоном (выбора нет), у 2 190 микрофонов несколько — и из них 1 562 ячейка
     * Mic/DI называет ровно один, а 620 не похожи ни на один. Из этих 620 четыреста девяносто
     * четыре — «TG57c» против «Beyerdynamic TG-D57C»: одна модель, разное написание. Ссылка
     * снимает весь этот класс отказов разом: у бочки два входа и два микрофона, и каждый вход
     * знает СВОЙ, а не соседа.
     *
     * НЕОБЯЗАТЕЛЕН, и это не временная мера: документ, написанный до перехода, её не несёт, и
     * подбор по имени остаётся для него запасным путём. Пустая ссылка ЗАКОННА — владелец,
     * 18.10.2026: угаданная хуже пустой, а в редакторе она ставится одним щелчком в той же ячейке.
     *
     * Ссылается на `line_id` строки гира ТОГО ЖЕ узла; у выходов поля нет — выходу микрофон не
     * нужен (запрет 10: по выходам другая ситуация, их не трогаем).
     */
    gear_line_id: uuidField().optional(),
})
    .strict();
/**
 * `output_list` entry on a rider node. Deliberately omits `Phantom_power` (0/observed occurrences
 * on output_list in the corpus — RIDER_SCHEMA_SPEC.md §4.1) — `.strict()` rejects it if present,
 * which is the enforcement mechanism for that rule.
 */
export const OutputListItemSchema = z
    .object({
    ...audioListItemCommonShape,
    owner_uuid: uuidField(),
    Unconnected: unconnectedField(),
    linked_uuids: linkedUuidsField(),
    Stereo_link: stereoLinkField(),
})
    .strict();
/** Convenience union for call sites that accept either list's item shape. */
export const AudioListItemSchema = z.union([InputListItemSchema, OutputListItemSchema]);
/**
 * Preset variants: a preset body (`menu_presets.JSON`) is not yet instantiated into a scene, so
 * its list items cannot legitimately carry `owner_uuid` (there is no live node uuid to reference
 * yet — the node's own `uuid` is itself optional, see `PresetNodeSchema`). RIDER_SCHEMA_SPEC.md
 * §2.4 only states this explicitly for `gear_list`; we extrapolate the same "not yet instantiated"
 * reasoning to input/output list items for internal consistency (not itself corpus-verified —
 * flag alongside the other open items if it turns out preset list items DO carry owner_uuid).
 */
export const PresetInputListItemSchema = z
    .object({
    ...audioListItemCommonShape,
    Phantom_power: PhantomPowerSchema.optional(),
    owner_uuid: uuidField().optional(),
    linked_indices: linkedIndicesField(),
    Stereo_link: stereoLinkField(),
    /**
     * ССЫЛКА «ЧЕМУ СЛУЖИТ» У ПРЕСЕТА — см. `InputListItem.gear_line_id` (SCRUM-639, шаг 2).
     *
     * Та же ссылка, что у канала райдера, и указывает так же — на `line_id` строки гира ЭТОГО
     * узла. У пресета номер линии носит характер заготовки (см. `line_id`), поэтому ссылка
     * вместе с ним ПРИМИРЯЕТСЯ с документом при вставке: строка, попадающая в уже существующую
     * линию райдера, берёт ЕЁ номер, и ссылка канала переезжает туда же
     * (`rider.ts#insertPresetAsChild`, `preset-channel-gear-link.test.ts`).
     *
     * ЗАЧЕМ ОНА ЗДЕСЬ ВООБЩЕ. Бэкфилл 29.10.2026 идёт ТОЛЬКО по общей библиотеке (владелец:
     * «пресеты до общей библиотеки, райдеры старые не трогаем»), значит пресет — единственный
     * путь, которым ссылка доходит до документа человека. Без этого поля `.strict()` отвергал
     * бы такой пресет целиком, и `GET /presets` перестал бы его отдавать — поймано спекой
     * `migrate-presets-to-container.e2e-spec.ts` 29.10.2026, шестью ошибками разом.
     */
    gear_line_id: uuidField().optional(),
})
    .strict();
export const PresetOutputListItemSchema = z
    .object({
    ...audioListItemCommonShape,
    owner_uuid: uuidField().optional(),
    linked_indices: linkedIndicesField(),
    Stereo_link: stereoLinkField(),
})
    .strict();
/**
 * THE LINK TO THE CATALOGUE (SCRUM-118 + SCRUM-565, 2.11.0). Owner 04.10.2026: «пока что это внутренний
 * каталог SoundCheckBox»; the model is a property of the INSTANCE. Owner 05.10.2026: «это должно быть так
 * beta52/d112/d6 и у нас уже 3 гира один основной и две альтернативы» — so the link is an ORDERED LIST
 * of `equipment.id`s (integers: the table keeps the v1 ids): the first is the model asked for, the rest
 * are alternatives the person named, in order of preference. One entry = exactly that model; several =
 * any of them will do — the length IS the strictness, so there is no separate «exact model» flag that
 * could contradict it. Distinct, at most 6. Optional: the 752 imported riders (free text) validate
 * unchanged. `[]` = the link was removed on purpose, which differs from absent. The schema cannot know
 * whether an id exists — a dangling id shows as «not in the catalogue», never a failed save
 * (`V2_EQUIPMENT_CATALOG.md` §5). A rental company's stock links to the same ids, so a rider stays
 * portable; the offer reads any model from the list as «as asked», not a deviation (rule 43).
 */
const equipmentIdsField = z
    .array(z.number().int().positive())
    .max(6)
    .refine((ids) => new Set(ids).size === ids.length, { message: "the models in one list must be distinct" })
    .optional();
const gearListItemCommonShape = {
    Name: z.string().min(1).max(255),
    Category: GearCategorySchema,
    Comment: z.string().max(2000),
    Image: z.string().max(255),
    Weight: z.number().min(0).nullable(),
    /**
     * TODO(sound-engineer): frequently fractional (0.25, 0.5) in the corpus — intent unconfirmed
     * (RIDER_SCHEMA_SPEC.md §4.2 anomaly #5). Typed as `number` (not integer) regardless of the
     * eventual answer, per spec.
     */
    Count: z.number().min(0).nullable(),
    Owner: OwnerSchema,
    Count_round: z.number().int().min(0),
    Weight_round: z.number().min(0),
    Power_consumption: z.number().int().min(0),
    /** A line a person added by hand carries its own models; a line that came with an object reads its
     *  object's (`RiderChildNode.equipment_ids`). SCRUM-118, 2.11.0. */
    equipment_ids: equipmentIdsField,
    /**
     * РУБРИКА ИЗ КАТАЛОГА — ССЫЛКОЙ, А НЕ ИМЕНЕМ (SCRUM-742, 2.15.0).
     *
     * Владелец, 12.10.2026: «рубрику дает каталог значит мы должны там писать рубрику каталога а не
     * категорию пресетов», «и она должна быть железно существующая».
     *
     * ПОЧЕМУ НОМЕР, А НЕ ТЕКСТ. В тот же вечер мы переименовали рубрики в самом каталоге
     * (`Drums&amp;Percussion` → `Drums & Percussion`, `d&amp;b` → `d&b`), и всякое СКОПИРОВАННОЕ в
     * райдер имя в тот же миг разошлось бы с каталогом — второй экземпляр правды, тот же изъян, что
     * был у имён оборудования. Имя показывается, но не хранится.
     *
     * ПОЧЕМУ НЕ ВЫВОДИТЬ НА ЛЕТУ из `equipment_ids`: позицию каталога могут слить с двойником (мы
     * слили шесть 12.10) или удалить, и тогда строка потеряла бы рубрику молча. Ссылка переживает
     * это и проверяется отдельно.
     *
     * НЕОБЯЗАТЕЛЬНА, и это не послабление: у строки БЕЗ связи с каталогом (то самое USER GEAR) и у
     * импортированных райдеров v1 другой правды нет — там остаётся открытый `Category`.
     */
    category_ref: z.number().int().positive().optional(),
    /**
     * КЛАСС КАТАЛОГА — ТО, ЧТО ЧЕЛОВЕК ПОПРОСИЛ, КОГДА НЕ НАЗВАЛ МОДЕЛЬ (SCRUM-750/566, 2.17.0).
     *
     * Владелец, 13.10.2026: «клавишная стойка это абстрактный пресет, класс», «если у нас что то
     * обозначает класс (keyboard stand) у нас должны быть варианты на него ответа», «у этих позиций
     * есть аналоги в реальном мире они должны быть описаны», и по форме — «то есть это задача для
     * каталога а не производитель нонаме».
     *
     * ПОЧЕМУ НЕ ПОЗИЦИЯ КАТАЛОГА. Класс не товар: производителя у него нет, и строка `equipment` под
     * «Noname» была бы враньём в данных — Noname значит «производитель неизвестен», то есть дыру.
     * Класс живёт своей сущностью: номер, имя, ОПИСАНИЕ настоящих аналогов, оси, по которым считается
     * членство.
     *
     * ПОЧЕМУ НЕ ОСЬ ПРЯМО В СТРОКЕ. Осей пять (`docs/V2_EQUIPMENT_CLASSIFICATION_BRIEF.md`), и поле
     * вида `{category_ref, stand_for}` означало бы ЗАПРОС, лежащий в данных: его нельзя ни назвать,
     * ни описать, ни показать человеку.
     *
     * РЯДОМ С `equipment_ids`, А НЕ ВМЕСТО. `class_ref` — вопрос («нужна клавишная стойка»),
     * `equipment_ids` — ответ («подойдёт K&M 18990»). Стереть вопрос, получив ответ, значит потерять
     * то, что человек просил: при замене модели прокатчиком сравнивать будет не с чем.
     */
    class_ref: z.number().int().positive().optional(),
    /**
     * НОМЕР ЛИНИИ — ТО, НА ЧТО ОТВЕЧАЕТ ПРОКАТЧИК (SCRUM-564, 2.16.0).
     *
     * Оффер отвечает не СТРОКЕ райдера, а СВЁРНУТОЙ ЛИНИИ: `summarizeGearList` складывает одинаковые
     * строки и кладёт их под один ключ. Замер 13.10.2026 по живым документам: 29 020 строк гира
     * складываются в 9 404 линии, в среднем 3.1 строки на линию; «microphone cable» — из 3848 строк.
     * Поэтому номер принадлежит ЛИНИИ и одинаков у всех её строк, а не строке.
     *
     * ЧТО ЭТО ЧИНИТ. До 2.16.0 ответ прокатчика держался за текстовый ключ
     * `gearOrderLineKey(category, owner, name)` (`offer.ts`). Переименовал гир, сменил владельца с
     * Rent на Band, слил одинаковые строки — ключ стал другим, и уже полученный ответ повис. А
     * правка райдера во время согласования это НОРМА, а не исключение: владелец, 12.10.2026 —
     * «пока контракт шоу не закрыт у нас идет согласование».
     *
     * ПРИ РАСПАДЕ ЛИНИИ (владелец, 13.10.2026, выбор «б»): строка, ушедшая из линии по имени,
     * получает НОВЫЙ номер и приходит к прокатчику как НЕОТВЕЧЕННАЯ. Ответ остаётся у той части,
     * что сохранила номер. Оффер про то, что привезут, и новая строка — новый вопрос.
     *
     * НЕОБЯЗАТЕЛЕН: документы, написанные до 2.16.0, его не несут, и это не ошибка — текстовый ключ
     * остаётся запасным путём для ответов, выданных до перехода. Проставляется миграцией
     * `scripts/mint-gear-line-ids.py` и дальше живёт в документе.
     *
     * У ПРЕСЕТА номер носит характер заготовки: при вставке строка, попадающая в УЖЕ СУЩЕСТВУЮЩУЮ
     * линию документа, берёт ЕЁ номер, а свой отбрасывает (`rider.ts#insertPresetAsChild`). Иначе
     * одна свёрнутая линия получила бы два номера.
     */
    line_id: uuidField().optional(),
};
/** `gear_list` entry on a rider node (instantiated — carries `owner_uuid`). */
export const RiderGearListItemSchema = z
    .object({
    ...gearListItemCommonShape,
    owner_uuid: uuidField(),
})
    .strict();
/**
 * `gear_list` entry on a preset node. `owner_uuid` must be ABSENT (0/514 sampled preset gear
 * items carry it — RIDER_SCHEMA_SPEC.md §2.4/§4.2); omitting it from the shape + `.strict()` is
 * the enforcement mechanism.
 */
export const PresetGearListItemSchema = z.object({ ...gearListItemCommonShape }).strict();
// ---------------------------------------------------------------------------------------------
// Node shapes: root, child (instantiated rider node), preset node. NOT the same shape — see
// RIDER_SCHEMA_SPEC.md §2.
// ---------------------------------------------------------------------------------------------
const nodeNameField = z.string().min(1).max(100);
const modelSrcField = z.string().min(1).max(255);
const titleField = z.string().max(255);
const commentField = z.string().max(5000);
/**
 * A node's footprint (`Width`/`Length`, SCRUM-457): a positive number of metres, finite, and no
 * larger than any real stage. Optional — see `RiderChildNode.Width`.
 */
const footprintField = z.number().positive().max(200).optional();
/** Both or neither: half a footprint is not a box (SCRUM-457). Shared by rider and preset nodes. */
function footprintPairIssue(node, ctx) {
    if ((node.Width === undefined) !== (node.Length === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [node.Width === undefined ? "Width" : "Length"],
            message: "Width and Length come together: a footprint is both or neither (SCRUM-457)",
        });
    }
}
/** The longest embedded image (characters of the data URL) — a ~400 KB JPEG, plenty for a face. */
export const LED_WALL_IMAGE_MAX_CHARS = 550_000;
/** The longest `media` string — an https address with a long query string still fits. */
export const LED_WALL_MEDIA_MAX_CHARS = 2048;
/** Where the server keeps what an administrator uploads; the document stores `PREFIX + name`. */
export const LED_WALL_MEDIA_UPLOAD_PREFIX = "/uploads/led-media/";
/** What an upload may weigh: a video up to 25 MiB, a picture or GIF up to 8 MiB. One place, because the
 *  server refuses over it and the editor tells the person BEFORE sending 25 MB to be refused. */
export const LED_WALL_MEDIA_MAX_VIDEO_BYTES = 25 * 1024 * 1024;
export const LED_WALL_MEDIA_MAX_IMAGE_BYTES = 8 * 1024 * 1024;
/** The file types an upload may be, by the extension the server gives the stored file. */
export const LED_WALL_MEDIA_UPLOAD_EXTENSIONS = ["mp4", "webm", "png", "jpg", "gif"];
/**
 * The SHAPE of a `media` string — one of three forms, nothing else:
 * 1. `scb:logo` | `scb:mascot` | `scb:intro` | `scb:intro-plain` | `scb:tutorial` (the two intros are the 20-second clips, with the money and without; SCRUM-492);
 * 2. `/uploads/led-media/<name>.<mp4|webm|png|jpg|gif>`, the name made of `a-z0-9-` only — exactly
 *    what the server stores, so no traversal, no query, no other folder;
 * 3. `https://<host>[:port][/path?query#hash]` with no credentials and no whitespace.
 * Whether form 3 is a YouTube or Vimeo page is a second check (`isStreamingPageUrl`): a case-insensitive
 * host test that a JSON Schema `pattern` cannot say.
 */
export const LED_WALL_MEDIA_PATTERN = /^(?:scb:(?:logo|mascot|intro-plain|intro|tutorial)|\/uploads\/led-media\/[a-z0-9][a-z0-9-]{0,100}\.(?:mp4|webm|png|jpg|gif)|https:\/\/[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.?(?::\d{1,5})?(?:[/?#]\S*)?)$/;
/** Hosts whose pages cannot be put on a surface in 3D: they hand out video only inside their own frame. */
const STREAMING_PAGE_DOMAINS = ["youtube.com", "youtu.be", "youtube-nocookie.com", "vimeo.com", "googlevideo.com", "vimeocdn.com"];
/** Is `value` an `https://` address on YouTube or Vimeo — or on the CDN host their players fetch the
 *  stream from (`googlevideo.com`, `vimeocdn.com`) — any subdomain, any capitalisation? */
export function isStreamingPageUrl(value) {
    const match = /^https:\/\/([^/?#:]+)/i.exec(value);
    if (!match)
        return false;
    const host = match[1].toLowerCase().replace(/\.$/, "");
    return STREAMING_PAGE_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}
/**
 * Every direct `https://` address a document would make a viewer's browser fetch (SCRUM-480, B1).
 *
 * The owner's rule is that a direct link may be set ONLY by an administrator. The shape check above
 * cannot say WHO wrote a string, so the server asks this function what a document contains and refuses
 * the write unless the caller is an administrator (apps/api `external-led-media.ts`).
 *
 * Deliberately a walk over the WHOLE value, not a walk down the known fields (`children`, `sets`,
 * `Construction`): the rule must hold for a rider, a preset node, a preset container and a Set inside a
 * Set alike, and for a field a later schema version adds. It looks for what is dangerous — an object with
 * `kind: "led_wall"` and a `media` string that starts `https:` (any case) — wherever it sits. A string
 * somewhere else (a title, a comment) is user text and is never a wall's media, so it is not reported.
 *
 * Total: any input, never throws; each address once, in first-seen order.
 */
export function findExternalLedMedia(document) {
    // NO cap on the walk, on purpose. It used to stop after 1,000,000 values and return what it had so far,
    // so a non-admin padded an unbounded array ahead of `children` and the link behind it went unseen
    // (security re-check of 1cf5908). A guard must fail closed, and a scan that gives up is not closed. The
    // walk is iterative (no stack overflow) and linear, and the API already caps the document's size;
    // `seen` stops a value that refers to itself.
    const found = new Set();
    const stack = [document];
    const seen = new WeakSet();
    while (stack.length > 0) {
        const value = stack.pop();
        if (value === null || typeof value !== "object")
            continue;
        if (seen.has(value))
            continue;
        seen.add(value);
        if (Array.isArray(value)) {
            for (let i = value.length - 1; i >= 0; i -= 1)
                stack.push(value[i]);
            continue;
        }
        const record = value;
        if (record.kind === "led_wall" && typeof record.media === "string" && /^https:/i.test(record.media.trim()))
            found.add(record.media);
        for (const key of Object.keys(record))
            stack.push(record[key]);
    }
    return [...found];
}
const ledWallMediaField = z
    .string()
    .min(1)
    .max(LED_WALL_MEDIA_MAX_CHARS)
    .regex(LED_WALL_MEDIA_PATTERN, "media must be scb:logo, scb:mascot, scb:intro, scb:intro-plain, scb:tutorial, /uploads/led-media/<name> or an https:// address")
    .refine((value) => !isStreamingPageUrl(value), { message: "a YouTube or Vimeo page cannot be shown on the wall — use a direct link to the file" })
    .optional();
const TrussConstructionSchema = z
    .object({
    kind: z.literal("truss"),
    truss: z.enum(["ladder", "triangle", "box"]),
    profile_mm: z.number().min(150).max(1000),
    length: z.number().positive().max(100),
    trim: z.number().min(0).max(40),
    support: z.enum(["flown", "floor", "goalpost"]),
})
    .strict();
const LedWallConstructionSchema = z
    .object({
    kind: z.literal("led_wall"),
    cabinet_w: z.number().min(0.1).max(3),
    cabinet_h: z.number().min(0.1).max(3),
    cols: z.number().int().min(1).max(400),
    rows: z.number().int().min(1).max(200),
    pitch_mm: z.number().positive().max(50).optional(),
    mount: z.enum(["ground", "flown"]),
    bottom: z.number().min(0).max(40),
    content: z.enum(["off", "grid", "bars", "logo", "image", "media"]).optional(),
    image: z
        .string()
        .max(LED_WALL_IMAGE_MAX_CHARS)
        .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]*$/)
        .optional(),
    media: ledWallMediaField,
})
    .strict();
/** A wall that shows media must say which (the footprint pair's twin: one field needs the other). */
function mediaContentIssue(construction, ctx) {
    if (construction.kind === "led_wall" && construction.content === "media" && construction.media === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["media"],
            message: 'content "media" needs `media`: which logo, mascot, file or address to show (SCRUM-480)',
        });
    }
}
export const ConstructionSchema = z
    .discriminatedUnion("kind", [TrussConstructionSchema, LedWallConstructionSchema])
    .superRefine(mediaContentIssue);
// NB: the explicit 3-arg `z.ZodType<Output, ZodTypeDef, unknown>` (rather than the 1-arg form)
// decouples the schema's static "input" type from its "output" type. This is required because
// `OwnerSchema`/`GearCategorySchema` use `z.preprocess`, whose _input_ type is intentionally
// wider (`unknown`) than its output (`string`) — the 1-arg form would otherwise force TS to
// require the input type to exactly equal the output interface, which a preprocess step can
// never satisfy structurally.
export const RiderChildNodeSchema = z.lazy(() => z
    .object({
    uuid: uuidField(),
    name: nodeNameField,
    model_src: modelSrcField,
    X: z.number(),
    Y: z.number(),
    Z: z.number(),
    turn: z.number(),
    Title: titleField,
    Comment: commentField,
    input_list: z.array(InputListItemSchema),
    output_list: z.array(OutputListItemSchema),
    gear_list: z.array(RiderGearListItemSchema),
    children: z.array(RiderChildNodeSchema),
    // `z.lazy` because `RiderSetNodeSchema` is declared further down and would be undefined at
    // module-evaluation time — the same reason `Rider.sets` uses it.
    sets: z.lazy(() => z.array(RiderSetNodeSchema)).optional(),
    Is_Structure: z.boolean(),
    Height: z.number(),
    Width: footprintField,
    Length: footprintField,
    Construction: ConstructionSchema.optional(),
    Snap_To_Grid: z.boolean().optional(),
    Locked: z.boolean(),
    Title_to_Source: z.boolean(),
    parent: uuidField().optional(),
    is_jump: z.boolean().optional(),
    sourcePresetId: z.string().optional(),
    sourceCategoryIds: z.array(z.number()).optional(),
    sourcePresetIcon: z.string().optional(),
    equipment_ids: equipmentIdsField,
})
    .strict()
    .superRefine(footprintPairIssue));
export const RiderSchema = z
    .object({
    schema_version: z
        .number()
        .int()
        .min(1)
        .describe("1 = legacy (no uuid). 2 = this (uuid-based) shape. This package validates schema_version=2 documents only."),
    uuid: uuidField(),
    name: nodeNameField,
    model_src: z.literal("my_project.json"),
    X: z.literal(0),
    Y: z.literal(0),
    Z: z.literal(0),
    turn: z.number(),
    Title: titleField,
    Comment: commentField,
    input_list: z.array(InputListItemSchema),
    output_list: z.array(OutputListItemSchema),
    gear_list: z.array(RiderGearListItemSchema),
    children: z.array(RiderChildNodeSchema),
    // `z.lazy` because `RiderSetNodeSchema` is declared BELOW this object and would otherwise be
    // read in its temporal dead zone at module load. Deliberately not solved by moving the Set
    // block up: it reads as a self-contained unit next to `PresetContainer`, which is the thing it
    // mirrors.
    sets: z.lazy(() => z.array(RiderSetNodeSchema)).optional(),
    Show_Grid: z.boolean(),
    Show_Channels: z.boolean(),
    Show_CH: z.boolean(),
    Show_MIX: z.boolean(),
    Show_Title: z.boolean(),
    Show_Comments: z.boolean(),
    /** Added 2026-08-01 (docs/V2_POST_CUTOVER_ROUND2.md #6) — show each input line's `Source` in the
     *  object's scene label. OPTIONAL, unlike its five siblings, so the 750 imported v1 documents
     *  (which predate the flag) keep validating untouched; absent reads as `false`. */
    Show_Source: z.boolean().optional(),
    gear_categories: z.array(z.string().max(100)),
    Rider_text: z.string().max(200_000),
    Length: z.number(),
    Width: z.number().min(0).optional(),
    Band_link: z.string().max(500).optional(),
    Contact_name: z.string().max(200).optional(),
    Contact_phone: z.string().max(100).optional(),
    Contact_email: z.string().max(200).optional(),
    Band_name: z.string().max(200).optional(),
    Band_logo: z.string().max(2_000_000).optional(),
    Use_border: z.boolean().optional(),
    Mixing_system: MixingSystemSchema.optional(),
    PA: PASchema.optional(),
})
    .strict();
export const PresetNodeSchema = z.lazy(() => z
    .object({
    uuid: uuidField().optional(),
    name: nodeNameField,
    model_src: modelSrcField,
    X: z.number(),
    Y: z.number(),
    Z: z.number(),
    turn: z.number(),
    Title: titleField,
    Comment: commentField,
    input_list: z.array(PresetInputListItemSchema),
    output_list: z.array(PresetOutputListItemSchema),
    gear_list: z.array(PresetGearListItemSchema),
    children: z.array(PresetNodeSchema),
    Is_Structure: z.boolean(),
    Height: z.number(),
    Width: footprintField,
    Length: footprintField,
    Construction: ConstructionSchema.optional(),
    Snap_To_Grid: z.boolean().optional(),
    Locked: z.boolean(),
    Title_to_Source: z.boolean(),
    equipment_ids: equipmentIdsField,
    parent: uuidField().optional(),
    is_jump: z.boolean().optional(),
})
    .strict()
    .superRefine(footprintPairIssue));
/** A preset = one child node (per menu_presets.JSON — RIDER_SCHEMA_SPEC.md §2.4). */
export const PresetSchema = PresetNodeSchema;
// ---------------------------------------------------------------------------------------------
// PresetContainer (SCRUM-160/162, decided 2026-08-09): the real, non-object container root that
// replaces "root = an arbitrary participant object" — same "root without a mesh" pattern already
// used by `Rider` (scene.ts:794, `rider.children.forEach(addRecursively)` never touches the root
// itself). Purely ADDITIVE: `PresetSchema`/`PresetNode` above are UNCHANGED and remain the shape
// of every already-imported catalog document until the migration (SCRUM-164) runs; this is the
// target shape that migration writes documents INTO.
//
// Used for BOTH of Janis's two terms (SCRUM-160 comment, 2026-08-09) — the shape is identical,
// only WHERE the document lives differs:
//  - a **Preset**: a catalog entry (`menu_presets.JSON`), not yet on any stage.
//  - a **Set**: the same shape once instantiated inside a Rider's own tree.
// No separate `SetSchema` — a distinct TS type here would just be the same fields with a
// different name, and the API layer (SCRUM-165) already has to know which table/context it's
// reading from regardless.
// ---------------------------------------------------------------------------------------------
/**
 * A single row-order entry: which descendant node OWNS the line, and that node's own array
 * position within ITS OWN `input_list`/`output_list`/`gear_list` — not a duplicate of the row
 * data itself (that still lives on the owning `PresetNode`), and not a new per-line uuid (avoids
 * a corpus-wide migration of every list-item shape for this). `owner` + `index` is already a
 * stable compound key: node `uuid` is stable, and a node's OWN list array order is stable at
 * rest — only the cross-tree flatten/traversal order this field replaces was ever unstable
 * (SCRUM-159).
 */
export const PresetLineRefSchema = z
    .object({
    owner: uuidField(),
    index: z.number().int().min(0),
})
    .strict();
export const PresetContainerSchema = z.lazy(() => z
    .object({
    uuid: uuidField().optional(),
    Title: titleField,
    Comment: commentField,
    // The angle the group was saved at — see `PresetContainer.turn`.
    turn: z.number().optional(),
    image: z.string().max(2_000_000).optional(),
    children: z.array(PresetNodeSchema),
    input_order: z.array(PresetLineRefSchema).optional(),
    output_order: z.array(PresetLineRefSchema).optional(),
    gear_order: z.array(PresetLineRefSchema).optional(),
    parents: z.array(uuidField()).optional(),
})
    .strict());
export const RiderSetNodeSchema = z.lazy(() => z
    .object({
    uuid: uuidField(),
    Title: titleField,
    Comment: commentField,
    X: z.number(),
    Y: z.number(),
    Z: z.number(),
    turn: z.number(),
    children: z.array(RiderChildNodeSchema),
    sets: z.array(RiderSetNodeSchema).optional(),
    input_order: z.array(PresetLineRefSchema).optional(),
    output_order: z.array(PresetLineRefSchema).optional(),
    gear_order: z.array(PresetLineRefSchema).optional(),
    parent: uuidField().optional(),
    sourcePresetId: z.string().optional(),
    sourceCategoryIds: z.array(z.number()).optional(),
    sourcePresetIcon: z.string().optional(),
})
    .strict());
//# sourceMappingURL=schema.js.map